import React, { useState, useRef, useEffect } from "react";
import useSeo from "@/hooks/useSeo";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, Lock, Eye, EyeOff, Tag, User as UserIcon, Building2,
  Briefcase, MapPin, Loader2, FileText, CheckCircle2, AlertCircle, ArrowRight,
  Sparkles, ShieldCheck, Zap, Coins, Timer,
} from "lucide-react";
import { api } from "@/lib/api";
import { ITALIAN_PROVINCES } from "@/lib/fiscal-constants";

const DASABO_LOGO = "/logo.png";

const EMPTY = {
  email: "", password: "", passwordConfirm: "", referral_code: "",
  nome: "", cognome: "",
  partita_iva: "", codice_fiscale: "", ragione_sociale: "", attivita: "",
  pec: "", codice_destinatario: "",
  indirizzo: "", numero_civico: "", cap: "", citta: "", provincia: "",
};

const SECTIONS = [
  { n: "01", label: "Accesso", icon: Lock, accent: "blue" },
  { n: "02", label: "Chi sei", icon: UserIcon, accent: "emerald" },
  { n: "03", label: "Azienda", icon: Building2, accent: "coral" },
  { n: "04", label: "Sede", icon: MapPin, accent: "ink" },
];

const SIDE_HIGHLIGHTS = [
  { icon: Coins, label: "5 crediti omaggio", body: "Basta il signup. Nessuna carta di credito richiesta." },
  { icon: Zap, label: "12 secondi al PDF", body: "L'AI legge il fornitore, l'imponibile e la valuta. Tu confermi." },
  { icon: ShieldCheck, label: "Server UE, GDPR-first", body: "Token cifrati Fernet AES-128. Nessun subprocessor USA." },
  { icon: Timer, label: "Nessun abbonamento", body: "Compri i crediti quando servono. Non scadono mai." },
];

export default function Register() {
  useSeo({
    title: "Registrati · 5 crediti omaggio",
    description: "Crea il tuo account Autofattura by Dasabo in 30 secondi. 5 crediti gratuiti per elaborare le prime autofatture estere con l'AI: Google Ads, Meta, Amazon, AWS. Servizio riservato a partite IVA italiane.",
    path: "/register",
  });
  useRevealOnScroll();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lookup, setLookup] = useState({ status: "idle", message: "" });
  const [errors, setErrors] = useState({});
  const debounceRef = useRef(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updUpper = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value.toUpperCase() }));

  // Debounced VAT lookup when P.IVA is 11 digits
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const piva = (form.partita_iva || "").replace(/\D/g, "");
    if (piva.length !== 11) {
      setLookup({ status: "idle", message: "" });
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLookup({ status: "loading", message: "Recupero dati aziendali dal VIES…" });
      try {
        const { data } = await api.get(`/vat-lookup/${piva}`);
        if (data.valid) {
          setForm((f) => ({
            ...f,
            ragione_sociale: f.ragione_sociale || data.denominazione || "",
            indirizzo: f.indirizzo || data.indirizzo || "",
            cap: f.cap || data.cap || "",
            citta: f.citta || data.comune || "",
            provincia: f.provincia || data.provincia || "",
          }));
          setLookup({
            status: "ok",
            message: `Dati aziendali recuperati da ${data.source || "VIES"}`,
          });
        } else {
          setLookup({
            status: "warn",
            message: data.error || "P.IVA non trovata negli archivi UE (VIES)",
          });
        }
      } catch (e) {
        setLookup({
          status: "warn",
          message: e?.response?.data?.detail || "Impossibile verificare la P.IVA",
        });
      }
    }, 700);
    return () => clearTimeout(debounceRef.current);
  }, [form.partita_iva]);

  const validate = () => {
    const err = {};
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Email non valida";
    if (!form.password || form.password.length < 6) err.password = "Almeno 6 caratteri";
    if (form.password !== form.passwordConfirm) err.passwordConfirm = "Le password non coincidono";
    if (!form.nome?.trim()) err.nome = "Inserisci il nome";
    if (!form.cognome?.trim()) err.cognome = "Inserisci il cognome";
    if (!form.ragione_sociale?.trim()) err.ragione_sociale = "Inserisci la ragione sociale";
    const piva = (form.partita_iva || "").replace(/\D/g, "");
    if (piva.length !== 11) err.partita_iva = "P.IVA italiana (11 cifre) richiesta";
    const cf = (form.codice_fiscale || "").trim().toUpperCase();
    if (cf.length !== 11 && cf.length !== 16) err.codice_fiscale = "Codice fiscale (11 o 16 caratteri) richiesto";
    if (!form.pec || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.pec)) err.pec = "PEC valida richiesta";
    if (!form.indirizzo?.trim()) err.indirizzo = "Indirizzo richiesto";
    if (!form.numero_civico?.trim()) err.numero_civico = "Numero civico richiesto";
    if (!/^\d{5}$/.test((form.cap || "").trim())) err.cap = "CAP di 5 cifre richiesto";
    if (!form.citta?.trim()) err.citta = "Città richiesta";
    if (!/^[A-Z]{2}$/.test((form.provincia || "").trim().toUpperCase())) err.provincia = "Provincia (sigla 2 lettere)";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Controlla i campi evidenziati");
      // Scroll to first error
      const firstErr = document.querySelector('[data-invalid="true"]');
      if (firstErr) firstErr.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        full_name: `${form.nome.trim()} ${form.cognome.trim()}`.trim(),
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        referral_code: form.referral_code?.trim() || null,
        partita_iva: (form.partita_iva || "").replace(/\D/g, ""),
        codice_fiscale: form.codice_fiscale?.trim().toUpperCase() || null,
        ragione_sociale: form.ragione_sociale?.trim() || null,
        attivita: form.attivita?.trim() || null,
        pec: form.pec?.trim() || null,
        codice_destinatario: form.codice_destinatario?.trim().toUpperCase() || null,
        indirizzo: form.indirizzo?.trim() || null,
        numero_civico: form.numero_civico?.trim() || null,
        cap: form.cap?.trim() || null,
        citta: form.citta?.trim() || null,
        provincia: form.provincia?.trim().toUpperCase() || null,
      };
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_user", JSON.stringify(data.user));
      toast.success("Account creato · 5 crediti omaggio caricati.");
      window.location.href = "/app";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore in fase di registrazione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#0A0A0A] overflow-x-hidden" data-testid="register-page">
      {/* NAV */}
      <header className="relative z-30 max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2 group" data-testid="register-logo">
          <span className="font-display font-extrabold text-lg tracking-tight flex items-center gap-1.5 text-[#0A0A0A]">
            Autofattura by
            <img src={DASABO_LOGO} alt="Dasabo · Autofattura estera" className="h-5 w-auto transition-transform group-hover:scale-105" />
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold text-[#4A4A4A] hover:text-[#0A0A0A] transition-colors hover-underline" data-testid="nav-accedi">
            Accedi
          </Link>
          <Link
            to="/"
            className="hidden sm:inline-flex px-4 py-2 rounded-full border border-[#0A0A0A]/15 text-sm font-semibold text-[#0A0A0A] hover:bg-white transition-colors"
          >
            Torna alla home
          </Link>
        </div>
      </header>

      {/* MAIN */}
      <div className="relative">
        {/* Background orbs */}
        <div className="mesh-orbs pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-6">
          {/* Compact hero */}
          <div className="reveal text-center mb-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E8E4D9] text-[#0A0A0A] text-xs font-semibold mb-5 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <Coins className="w-3.5 h-3.5 text-emerald-600" /> 5 crediti omaggio inclusi
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.95] mb-4">
              Attiva il tuo{" "}
              <span className="relative inline-block">
                <span className="relative z-10 font-serif-accent text-[#1D4ED8]">account</span>
                <span aria-hidden="true" className="absolute inset-x-0 bottom-1 h-3 bg-[#1D4ED8]/20 -skew-x-6 z-0"></span>
              </span>
              <br />
              <span className="text-[#4A4A4A] text-2xl sm:text-3xl md:text-4xl">in 30 secondi.</span>
            </h1>
            <p className="text-sm md:text-base text-[#4A4A4A] leading-relaxed max-w-xl mx-auto mb-3">
              Servizio riservato a partite IVA italiane. Il primo credito lo consumi appena provi con un PDF vero — la nostra AI legge il fornitore, sceglie il codice TD e genera l&apos;XML in 12 secondi.
            </p>
            <p className="text-xs text-[#4A4A4A]">
              Hai già un account?{" "}
              <Link to="/login" className="font-semibold text-[#1D4ED8] hover:underline hover-underline" data-testid="goto-login">
                Accedi da qui
              </Link>
            </p>
          </div>

          {/* Progress rail */}
          <div className="reveal flex items-center gap-2 mb-6 overflow-x-auto pb-2 justify-center">
            {SECTIONS.map((s, i) => (
              <a
                key={s.n}
                href={`#sec-${s.n}`}
                className="group flex items-center gap-2 shrink-0 px-3 py-1.5 rounded-full bg-white border border-[#E8E4D9] text-xs font-semibold text-[#4A4A4A] hover:border-[#0A0A0A]/30 hover:text-[#0A0A0A] transition-colors"
                data-testid={`progress-jump-${s.n}`}
              >
                <span className="font-mono text-[10px] text-[#B8985C]">{s.n}</span>
                <s.icon className="w-3.5 h-3.5" />
                <span>{s.label}</span>
                {i < SECTIONS.length - 1 && <span className="text-[#B8985C]/50 ml-1">→</span>}
              </a>
            ))}
          </div>

          <form onSubmit={submit} noValidate className="reveal reveal-delay-1" data-testid="register-form">
            <div className="rounded-3xl bg-white border border-[#E8E4D9] shadow-[0_20px_50px_-24px_rgba(10,10,10,0.15)] p-6 sm:p-8 md:p-10 space-y-10">
              {/* --- Section 1: Credentials --- */}
              <RegSection n="01" icon={Lock} title="Credenziali di accesso" accent="blue" subtitle="Con queste ti loggherai. La password è cifrata in bcrypt.">
                  <Field label="Email" required error={errors.email}>
                    <InputWithIcon
                      icon={<Mail className="w-4 h-4" />}
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={upd("email")}
                      placeholder="tu@studio.it"
                      testid="register-email"
                      invalid={!!errors.email}
                    />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Password" required error={errors.password}>
                      <InputWithIcon
                        icon={<Lock className="w-4 h-4" />}
                        trailing={
                          <button
                            type="button"
                            onClick={() => setShowPass((v) => !v)}
                            className="text-zinc-400 hover:text-zinc-700"
                            tabIndex={-1}
                            data-testid="toggle-password"
                            aria-label={showPass ? "Nascondi password" : "Mostra password"}
                          >
                            {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        }
                        type={showPass ? "text" : "password"}
                        autoComplete="new-password"
                        value={form.password}
                        onChange={upd("password")}
                        placeholder="Almeno 6 caratteri"
                        testid="register-password"
                        invalid={!!errors.password}
                      />
                    </Field>
                    <Field label="Conferma password" required error={errors.passwordConfirm}>
                      <InputWithIcon
                        icon={<Lock className="w-4 h-4" />}
                        trailing={
                          <button
                            type="button"
                            onClick={() => setShowPass2((v) => !v)}
                            className="text-zinc-400 hover:text-zinc-700"
                            tabIndex={-1}
                            data-testid="toggle-password2"
                            aria-label={showPass2 ? "Nascondi conferma password" : "Mostra conferma password"}
                          >
                            {showPass2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        }
                        type={showPass2 ? "text" : "password"}
                        autoComplete="new-password"
                        value={form.passwordConfirm}
                        onChange={upd("passwordConfirm")}
                        placeholder="Ripeti"
                        testid="register-password-confirm"
                        invalid={!!errors.passwordConfirm}
                      />
                    </Field>
                  </div>
                  <Field label="Codice referral" labelSuffix="— facoltativo" hint="Se un amico ti ha invitato, incolla qui il codice. Quando fai il primo acquisto ricevete +5 crediti a testa.">
                    <InputWithIcon
                      icon={<Tag className="w-4 h-4" />}
                      value={form.referral_code}
                      onChange={upd("referral_code")}
                      placeholder="Es. AF-K7X9-2M"
                      testid="register-referral"
                      mono
                    />
                  </Field>
                </RegSection>

                {/* --- Section 2: Personal --- */}
                <RegSection n="02" icon={UserIcon} title="Chi sei" accent="emerald" subtitle="Nome e cognome finiscono nella fattura fiscale che emetteremo per te.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Nome" required error={errors.nome}>
                      <InputWithIcon
                        icon={<UserIcon className="w-4 h-4" />}
                        value={form.nome}
                        onChange={upd("nome")}
                        placeholder="Mario"
                        testid="register-nome"
                        invalid={!!errors.nome}
                      />
                    </Field>
                    <Field label="Cognome" required error={errors.cognome}>
                      <InputWithIcon
                        icon={<UserIcon className="w-4 h-4" />}
                        value={form.cognome}
                        onChange={upd("cognome")}
                        placeholder="Rossi"
                        testid="register-cognome"
                        invalid={!!errors.cognome}
                      />
                    </Field>
                  </div>
                </RegSection>

                {/* --- Section 3: Company --- */}
                <RegSection n="03" icon={Building2} title="La tua azienda o P.IVA" accent="coral" subtitle="Un solo cliente/azienda su questo account. Se sei commercialista e devi gestire più clienti, scrivici dopo il signup per l'attivazione studio.">
                  <Field
                    label="Partita IVA italiana"
                    required
                    error={errors.partita_iva}
                    hint="Appena digiti 11 cifre valide interroghiamo VIES/Agenzia Entrate e pre-compiliamo ragione sociale e sede."
                  >
                    <InputWithIcon
                      icon={<Building2 className="w-4 h-4" />}
                      trailing={
                        lookup.status === "loading" ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> :
                        lookup.status === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
                        lookup.status === "warn" ? <AlertCircle className="w-4 h-4 text-amber-600" /> : null
                      }
                      inputMode="numeric"
                      maxLength={11}
                      value={form.partita_iva}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, partita_iva: e.target.value.replace(/\D/g, "").slice(0, 11) }))
                      }
                      placeholder="Es. 07407380828"
                      mono
                      testid="register-piva"
                      invalid={!!errors.partita_iva}
                    />
                    {lookup.status !== "idle" && lookup.message && (
                      <div
                        data-testid="lookup-status"
                        className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                          lookup.status === "ok"
                            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : lookup.status === "warn"
                            ? "bg-amber-50 text-amber-800 border-amber-200"
                            : "bg-blue-50 text-blue-800 border-blue-200"
                        }`}
                      >
                        {lookup.status === "loading" && <Loader2 className="w-3 h-3 animate-spin" />}
                        {lookup.status === "ok" && <CheckCircle2 className="w-3 h-3" />}
                        {lookup.status === "warn" && <AlertCircle className="w-3 h-3" />}
                        <span>{lookup.message}</span>
                      </div>
                    )}
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Codice fiscale" required error={errors.codice_fiscale}>
                      <InputWithIcon
                        icon={<FileText className="w-4 h-4" />}
                        value={form.codice_fiscale}
                        onChange={updUpper("codice_fiscale")}
                        placeholder="11 o 16 caratteri"
                        mono
                        maxLength={16}
                        testid="register-cf"
                        invalid={!!errors.codice_fiscale}
                      />
                    </Field>
                    <Field label="PEC" required error={errors.pec}>
                      <InputWithIcon
                        icon={<Mail className="w-4 h-4" />}
                        type="email"
                        value={form.pec}
                        onChange={upd("pec")}
                        placeholder="tu@pec.it"
                        testid="register-pec"
                        invalid={!!errors.pec}
                      />
                    </Field>
                  </div>

                  <Field label="Ragione sociale" required error={errors.ragione_sociale}>
                    <InputWithIcon
                      icon={<Building2 className="w-4 h-4" />}
                      value={form.ragione_sociale}
                      onChange={upd("ragione_sociale")}
                      placeholder="Nome ditta / studio / ragione sociale"
                      testid="register-ragione-sociale"
                      invalid={!!errors.ragione_sociale}
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Attività" labelSuffix="— facoltativo">
                      <InputWithIcon
                        icon={<Briefcase className="w-4 h-4" />}
                        value={form.attivita}
                        onChange={upd("attivita")}
                        placeholder="Es. web agency"
                        testid="register-attivita"
                      />
                    </Field>
                    <Field
                      label="Codice destinatario SdI"
                      labelSuffix="— facoltativo"
                      hint="Se lo hai, l'autofattura verrà recapitata lì (stato: Consegnata). In alternativa usiamo la PEC."
                    >
                      <InputWithIcon
                        icon={<Tag className="w-4 h-4" />}
                        value={form.codice_destinatario}
                        onChange={updUpper("codice_destinatario")}
                        placeholder="Es. M5UXCR1"
                        mono
                        maxLength={7}
                        testid="register-sdi"
                      />
                    </Field>
                  </div>
                </RegSection>

                {/* --- Section 4: Address --- */}
                <RegSection n="04" icon={MapPin} title="Sede legale" accent="ink" subtitle="Serve per l'anagrafica dell'XML FatturaPA che generiamo per te.">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                      <Field label="Via / Piazza" required error={errors.indirizzo}>
                        <InputWithIcon
                          icon={<MapPin className="w-4 h-4" />}
                          value={form.indirizzo}
                          onChange={upd("indirizzo")}
                          placeholder="Via Imperatore Federico"
                          testid="register-indirizzo"
                          invalid={!!errors.indirizzo}
                        />
                      </Field>
                    </div>
                    <Field label="N. civico" required error={errors.numero_civico}>
                      <InputWithIcon
                        icon={<MapPin className="w-4 h-4" />}
                        value={form.numero_civico}
                        onChange={upd("numero_civico")}
                        placeholder="100"
                        testid="register-civico"
                        invalid={!!errors.numero_civico}
                        mono
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                    <div className="sm:col-span-2">
                      <Field label="CAP" required error={errors.cap}>
                        <InputWithIcon
                          icon={<MapPin className="w-4 h-4" />}
                          value={form.cap}
                          onChange={upd("cap")}
                          placeholder="90143"
                          mono
                          maxLength={5}
                          inputMode="numeric"
                          testid="register-cap"
                          invalid={!!errors.cap}
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-3">
                      <Field label="Città" required error={errors.citta}>
                        <InputWithIcon
                          icon={<MapPin className="w-4 h-4" />}
                          value={form.citta}
                          onChange={upd("citta")}
                          placeholder="Palermo"
                          testid="register-citta"
                          invalid={!!errors.citta}
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-1">
                      <Field label="PR" required error={errors.provincia}>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                            <MapPin className="w-4 h-4" />
                          </div>
                          <select
                            value={form.provincia}
                            onChange={upd("provincia")}
                            data-testid="register-provincia"
                            data-invalid={!!errors.provincia}
                            className={`w-full h-11 pl-9 pr-3 rounded-lg border bg-white focus:ring-2 outline-none text-sm font-mono transition-colors ${
                              errors.provincia
                                ? "border-red-400 focus:border-red-500 focus:ring-red-500/10"
                                : "border-zinc-300 focus:border-blue-600 focus:ring-blue-600/10"
                            }`}
                            aria-label="Sigla provincia"
                          >
                            <option value="">--</option>
                            {ITALIAN_PROVINCES.map((p) => (
                              <option key={p.code} value={p.code}>{p.code}</option>
                            ))}
                          </select>
                        </div>
                      </Field>
                    </div>
                  </div>
                </RegSection>

                {/* Sticky-ish CTA */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="register-submit-btn"
                    className="group w-full inline-flex items-center justify-center gap-3 h-14 rounded-full bg-[#0A0A0A] text-white font-bold text-base hover:bg-[#1D4ED8] shadow-2xl shadow-black/25 disabled:opacity-70 transition-all overflow-hidden relative"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                    />
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Sto creando l&apos;account…</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 text-emerald-300" />
                        Crea account · 5 crediti omaggio
                        <span className="w-7 h-7 rounded-full bg-white/15 grid place-items-center group-hover:translate-x-0.5 transition-transform">
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </>
                    )}
                  </button>
                  <p className="mt-4 text-xs text-[#737373] text-center leading-relaxed">
                    Continuando accetti i nostri{" "}
                    <Link to="/terms" className="underline hover:text-[#0A0A0A]">Termini</Link>,{" "}
                    la <Link to="/privacy" className="underline hover:text-[#0A0A0A]">Privacy Policy</Link>{" "}
                    e la <Link to="/cookies" className="underline hover:text-[#0A0A0A]">Cookie Policy</Link>.
                    Server UE. Nessun subprocessor USA.
                  </p>
                </div>
              </div>
            </form>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- */
/* --------------------------- Sub-components ------------------------ */
/* ------------------------------------------------------------------- */

const ACCENT_COLOR = {
  blue: "text-[#1D4ED8]",
  emerald: "text-[#059669]",
  coral: "text-[#F97316]",
  ink: "text-[#0A0A0A]",
};

function RegSection({ n, icon: Icon, title, subtitle, accent = "blue", children }) {
  const c = ACCENT_COLOR[accent] || ACCENT_COLOR.blue;
  return (
    <section id={`sec-${n}`} className="scroll-mt-24">
      <div className="flex items-start gap-4 mb-6 pb-4 border-b border-[#E8E4D9]">
        <div className={`font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none ${c} opacity-90`}>{n}</div>
        <div className="flex-1 pt-1">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${c}`} />
            <h2 className="font-display font-bold text-xl sm:text-2xl text-[#0A0A0A] tracking-tight">{title}</h2>
          </div>
          {subtitle && <p className="mt-1.5 text-sm text-[#737373] leading-relaxed">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, labelSuffix, required, error, hint, children }) {
  return (
    <div>
      <label className="flex items-baseline gap-1.5 text-sm font-semibold text-[#0A0A0A] mb-1.5">
        <span>{label}</span>
        {labelSuffix && <span className="text-xs text-[#737373] font-normal">{labelSuffix}</span>}
        {required && <span className="text-red-500" aria-label="obbligatorio">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" role="alert">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}
      {hint && !error && <p className="mt-1.5 text-xs text-[#737373] leading-relaxed">{hint}</p>}
    </div>
  );
}

function InputWithIcon({ icon, trailing, mono, invalid, testid, className = "", ...props }) {
  return (
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
          {icon}
        </div>
      )}
      <input
        data-testid={testid}
        data-invalid={invalid ? "true" : undefined}
        className={`w-full h-11 ${icon ? "pl-9" : "pl-3"} ${trailing ? "pr-10" : "pr-3"} rounded-lg border ${
          invalid
            ? "border-red-400 focus:border-red-500 focus:ring-red-500/10 bg-red-50/30"
            : "border-zinc-300 focus:border-[#1D4ED8] focus:ring-[#1D4ED8]/10 bg-white"
        } text-sm focus:ring-2 outline-none transition-all placeholder:text-zinc-400 ${mono ? "font-mono" : ""} ${className}`}
        {...props}
      />
      {trailing && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {trailing}
        </div>
      )}
    </div>
  );
}
